import type { AgentTaskModel } from "@fencyai/js";
import { useAgentTasks } from "@fencyai/react";
import { useCallback, useRef } from "react";

type FencyMessage = { role: "SYSTEM" | "USER" | "ASSISTANT"; content: string };

type Props = {
  fetchClientToken: () => Promise<{ clientToken: string }>;
  timeoutMs?: number;
};

const DEFAULT_TIMEOUT_MS = 60_000;

/**
 * Plain (non-strict) streaming completion used to drive the planner loop.
 *
 * Unlike Fency's StructuredChatCompletion, a StreamingChatCompletion never fails with
 * "The model did not generate a valid response object" — it always returns text. We parse the
 * action JSON from that text ourselves, so the planner can't be taken down by strict-output
 * flakiness (and needs no retry). Mirrors useStructuredCompletion's error/timeout handling:
 * @fencyai/react settles createAgentTask only on "Completed", so an "Error"/stream failure must
 * reject the in-flight call explicitly.
 */
export const usePlannerCompletion = ({ fetchClientToken, timeoutMs = DEFAULT_TIMEOUT_MS }: Props) => {
  const pending = useRef(new Map<symbol, (reason: Error) => void>());

  const failAll = (error: Error) => {
    pending.current.forEach((reject) => reject(error));
    pending.current.clear();
  };

  const fetchToken = useCallback(
    () => fetchClientToken(),
    [fetchClientToken],
  );

  const { createAgentTask } = useAgentTasks({
    fetchCreateAgentTaskClientToken: fetchToken,
    onAgentTaskProgressItemUpdated: ({ data }) => {
      if (data.taskType === "StreamingChatCompletion" && data.eventType === "Error") {
        failAll(new Error(data.message));
      }
    },
    onStreamError: ({ error }) => failAll(new Error(error.message)),
  });

  return useCallback(
    async (messages: FencyMessage[], model: AgentTaskModel): Promise<string> => {
      const callId = Symbol();
      const timeout = setTimeout(() => pending.current.get(callId)?.(new Error("Planner timed out")), timeoutMs);
      try {
        const result = await Promise.race([
          createAgentTask({ type: "StreamingChatCompletion", messages, model }),
          new Promise<never>((_, reject) => pending.current.set(callId, reject)),
        ]);
        if (result.type !== "success") {
          throw new Error(result.error.message);
        }
        if (result.response.taskType !== "StreamingChatCompletion" || result.response.eventType !== "Completed") {
          throw new Error("Unexpected planner response.");
        }
        return result.response.response.textResponse;
      } finally {
        pending.current.delete(callId);
        clearTimeout(timeout);
      }
    },
    [createAgentTask, timeoutMs],
  );
};
